// Header navigation components
// Mobile nav drawer, submenu triggers, mobile nav triggers
class SubmenuTrigger extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.button = null;
  }

  connectedCallback() {
    // Find button inside or use the element itself
    this.button = this.querySelector('button') || this;
    
    if (!this.button) {
      console.warn('SubmenuTrigger: button element is required inside submenu-trigger');
      return;
    }

    const ariaExpanded = this.getAttribute('aria-expanded');
    const ariaControls = this.getAttribute('aria-controls');
    
    if (ariaExpanded && !this.button.hasAttribute('aria-expanded')) {
      this.button.setAttribute('aria-expanded', ariaExpanded);
    }
    if (ariaControls && !this.button.hasAttribute('aria-controls')) {
      this.button.setAttribute('aria-controls', ariaControls);
    }
      
    // Use button for interactions
    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button && this.button.tagName === 'BUTTON') {
      this.button.removeEventListener('click', this.handleClick);
    } else {
      this.removeEventListener('click', this.handleClick);
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  toggle() {
    // Always use button for ARIA attributes (best practice)
    const target = this.button || this;
    const isExpanded = target.getAttribute('aria-expanded') === 'true';
    const controlsId = target.getAttribute('aria-controls');
    
    if (!controlsId) return;

    // Close all other submenus
    this.closeOtherSubmenus(controlsId);

    // Toggle this submenu
    const newExpandedState = !isExpanded;
    
    // Update all buttons that control this submenu (aria-controls is on buttons now)
    const buttonsWithSameId = document.querySelectorAll(
      `button[aria-controls="${controlsId}"]`
    );
    
    buttonsWithSameId.forEach(btn => {
      // Update button's aria-expanded (the interactive element)
      btn.setAttribute('aria-expanded', newExpandedState ? 'true' : 'false');
    });

    // Update the submenu itself
    const submenu = document.getElementById(controlsId);
    if (submenu) {
      submenu.setAttribute('data-active', newExpandedState ? 'true' : 'false');
    }
  }

  closeOtherSubmenus(currentControlsId) {
    // Find all buttons with aria-controls (they're inside submenu-trigger custom elements)
    const allButtons = document.querySelectorAll('submenu-trigger button[aria-controls]');
    allButtons.forEach(btn => {
      const triggerControlsId = btn.getAttribute('aria-controls');
      if (triggerControlsId && triggerControlsId !== currentControlsId) {
        btn.setAttribute('aria-expanded', 'false');
        const submenu = document.getElementById(triggerControlsId);
        if (submenu) {
          submenu.setAttribute('data-active', 'false');
        }
      }
    });
  }
}

class MobileNavTrigger extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.button = null;
  }

  connectedCallback() {
    // Find button inside or use the element itself
    this.button = this.querySelector('button') || this;
    
    if (!this.button) {
      console.warn('MobileNavTrigger: button element is required inside mobile-nav-trigger');
      return;
    }

    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  toggle() {
    const isOpen = document.body.getAttribute('data-nav-open') === 'true';
    document.body.setAttribute('data-nav-open', !isOpen);
    
    // Reset drawer state when closing (after transition completes)
    if (isOpen) {
      setTimeout(() => {
        this.resetDrawer();
      }, 500);
    }
  }

  resetDrawer() {
    const drawer = document.querySelector('mobile-nav-drawer');
    if (!drawer) return;
    
    drawer.reset();
  }
}

class MobileNavDrawer extends HTMLElement {
  reset() {
    // Reset all submenu triggers inside the drawer
    const submenuTriggers = this.querySelectorAll('submenu-trigger');
    submenuTriggers.forEach(trigger => {
      const btn = trigger.querySelector('button') || trigger;
      btn.setAttribute('aria-expanded', 'false');
    });
    
    // Reset all accordion triggers inside the drawer
    const accordionTriggers = this.querySelectorAll('accordion-trigger');
    accordionTriggers.forEach(trigger => {
      const btn = trigger.querySelector('button') || trigger;
      btn.setAttribute('aria-expanded', 'false');
    });
    
    // Reset all data-active states inside the drawer
    const activeElements = this.querySelectorAll('[data-active]');
    activeElements.forEach(element => {
      element.setAttribute('data-active', 'false');
    });
  }
}

// Register header navigation components
customElements.define('submenu-trigger', SubmenuTrigger);
customElements.define('mobile-nav-trigger', MobileNavTrigger);
customElements.define('mobile-nav-drawer', MobileNavDrawer);

// Header height management
// Sets --header-height to the full height of the header group, which includes
// the announcement bar and any other sections placed above the main nav.
// ResizeObserver is used instead of a one-shot DOMContentLoaded measurement so
// that dynamic changes — such as the announcement-bar custom element upgrading
// after this module loads, or viewport resizes — are always reflected.
function setHeaderHeight() {
  const header = document.querySelector('header');
  if (!header) return;
  document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
};

const _header = document.querySelector('header');

if (_header && typeof ResizeObserver !== 'undefined') {
  new ResizeObserver(setHeaderHeight).observe(_header);
} else {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setHeaderHeight);
  } else {
    setHeaderHeight();
  }
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setHeaderHeight, 150);
  });
};

document.addEventListener('header:height-change', setHeaderHeight);